/**
 * Pure derivations for the Vendor dashboard "What needs your attention" insights (#716).
 *
 * Every value here is computed from the already-loaded vendor DTA list (`useVendorDtasList`) — no
 * new network calls. Kept free of React/rendering so the derivations can be unit-tested directly.
 */

import { WorkflowStage } from "@/generated/sdk"
import type { DTARecord } from "../../services/dtaApi"

/**
 * Days after which a pending action is emphasised as stale.
 */
export const STALE_ACTION_DAYS = 14

/**
 * Cap for the "pending your action the longest" list.
 */
export const OLDEST_PENDING_LIMIT = 5

const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * Stages that represent a required vendor action. APPROVED is informational (review/comment only)
 * and INACTIVE is read-only, so neither counts as "waiting on you".
 */
const ACTIONABLE_STAGES: readonly WorkflowStage[] = [WorkflowStage.Review, WorkflowStage.Active]

interface ActionCounts {
  /**
   * REVIEW — the vendor needs to review and sign. Highest priority (blocks activation).
   */
  toSign: number
  /**
   * ACTIVE - the vendor has an ongoing obligation to submit Live Run data.
   */
  toSubmit: number
  /**
   * APPROVED — informational; the vendor can review/comment but has no gating action.
   */
  inReview: number
}

/**
 * Counts the vendor's DTAs by the action each stage requires. O(n) over the loaded list.
 */
export function countByAction(dtas: DTARecord[]): ActionCounts {
  return dtas.reduce<ActionCounts>(
    (acc, dta) => {
      if (dta.workflow_stage === WorkflowStage.Review) acc.toSign += 1
      else if (dta.workflow_stage === WorkflowStage.Active) acc.toSubmit += 1
      else if (dta.workflow_stage === WorkflowStage.Approved) acc.inReview += 1
      return acc
    },
    { toSign: 0, toSubmit: 0, inReview: 0 },
  )
}

/**
 * Actionable DTAs (REVIEW + ACTIVE) sorted oldest-first by `last_modified_at`, capped at `limit`.
 *
 * This is intentionally the inverse of the main DTA table (which sorts newest-first): the point of
 * the widget is to surface what has been waiting the longest.
 */
export function oldestPending(dtas: DTARecord[], limit = OLDEST_PENDING_LIMIT): DTARecord[] {
  return dtas
    .filter((dta) => ACTIONABLE_STAGES.includes(dta.workflow_stage))
    .slice()
    .sort((a, b) => new Date(a.last_modified_at).getTime() - new Date(b.last_modified_at).getTime())
    .slice(0, limit)
}

/**
 * Whole days elapsed since `isoTimestamp`. Returns `null` for a missing or unparseable timestamp so
 * callers can render a placeholder rather than "NaN days".
 */
export function ageInDays(isoTimestamp: string, now: Date = new Date()): number | null {
  if (!isoTimestamp) return null
  const then = new Date(isoTimestamp).getTime()
  if (Number.isNaN(then)) return null
  return Math.max(0, Math.floor((now.getTime() - then) / MS_PER_DAY))
}

/**
 * Human-readable age of a pending item (e.g. "Today", "1 day", "14 days"). Returns "—" when the
 * timestamp is missing or unparseable.
 */
export function formatAge(isoTimestamp: string, now: Date = new Date()): string {
  const days = ageInDays(isoTimestamp, now)
  if (days === null) return "—"
  if (days === 0) return "Today"
  if (days === 1) return "1 day"
  return `${days} days`
}

/**
 * Whether a pending item has been waiting long enough to warrant emphasis.
 */
export function isStale(isoTimestamp: string, now: Date = new Date()): boolean {
  const days = ageInDays(isoTimestamp, now)
  return days !== null && days > STALE_ACTION_DAYS
}

/**
 * A single "by stage" donut slice: the stage label and how many DTAs sit in it.
 */
interface StageSlice {
  title: WorkflowStage
  value: number
}

/**
 * Donut slices for the "DTAs by stage" chart: one slice per stage in the given order, with
 * zero-count stages dropped (the donut expects callers to omit empty slices).
 */
export function toStageSlices(dtas: DTARecord[], stages: readonly WorkflowStage[]): StageSlice[] {
  return stages
    .map((stage) => ({
      title: stage,
      value: dtas.filter((dta) => dta.workflow_stage === stage).length,
    }))
    .filter((slice) => slice.value > 0)
}
